import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations } from "@/data/translations";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { getByPath } from "@/lib/objectPath";

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const { merged } = useSiteContent();

  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("kiwami_lang") || "fr";
    } catch {
      return "fr";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("kiwami_lang", lang);
      if (typeof document !== "undefined") {
        document.documentElement.lang = lang;
      }
    } catch {
      /* ignore */
    }
  }, [lang]);

  const toggleLang = useCallback(
    () => setLang((prev) => (prev === "fr" ? "en" : "fr")),
    [],
  );

  /**
   * Translation reader. Resolution order:
   *   1) Site content override at translations.<lang>.<path>
   *   2) Hard-coded translations[lang][path]
   *   3) The path string itself (debug fallback)
   */
  const t = useCallback(
    (path) => {
      // 1) Try server override
      const overridePath = `translations.${lang}.${path}`;
      const overridden = getByPath(merged, overridePath);
      if (overridden !== undefined && overridden !== null && overridden !== "") {
        return overridden;
      }
      // 2) Built-in defaults
      const keys = path.split(".");
      let val = translations[lang];
      for (const k of keys) {
        if (val && typeof val === "object" && k in val) {
          val = val[k];
        } else {
          return path; // not found
        }
      }
      return val;
    },
    [lang, merged],
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
